import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { apiUpload, api } from "@/lib/api";
import { SEASON } from "@/lib/constants";

// Calcola Giornata: carica Excel voti → anteprima modificabile risultati +
// rinvii → conferma → liquida tutti i giochi in un colpo.
export default function CalcolaGiornata() {
  const [md, setMd] = useState(1);
  const [mdTouched, setMdTouched] = useState(false); // l'admin ha cambiato a mano?
  const [file, setFile] = useState(null);
  const [votiPreview, setVotiPreview] = useState(null);
  const [imported, setImported] = useState(false);
  const [preview, setPreview] = useState(null);
  const [edits, setEdits] = useState({});         // key -> {home, away}
  const [postponed, setPostponed] = useState({}); // key -> bool (rinviata)
  const [suspended, setSuspended] = useState({}); // key -> bool (sospesa)
  const [pending, setPending] = useState({});     // key -> bool (in attesa)
  const [fsName, setFsName] = useState("");
  const [fsTeam, setFsTeam] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("full"); // full | sv_bonus | sv_tk_bonus
  const xlsxRef = useRef(null);

  const manual = mode !== "full";
  const gamesFor = mode === "sv_bonus" ? ["survival", "bonus_exact"]
    : mode === "sv_tk_bonus" ? ["survival", "tiket", "bonus_exact"]
    : null;
  const changeMode = (m) => { setMode(m); setPreview(null); setResult(null); };

  // Posiziona la console sulla giornata IN CORSO (non su G1/G2 già passate).
  useEffect(() => {
    let alive = true;
    api(`/admin/settle-matchday/current?season=${SEASON}`)
      .then((r) => { if (alive && !mdTouched && r?.matchday) setMd(r.matchday); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reopen = async () => {
    if (!window.confirm(`Riaprire la giornata ${md} già chiusa? Ripristina vite/eliminazioni allo stato d'inizio giornata e la rende di nuovo ricalcolabile. Un eventuale Round successivo SENZA giocate verrà eliminato.`)) return;
    setBusy(true);
    try {
      const r = await api("/admin/settle-matchday/reopen", { method: "POST", body: { matchday: Number(md), season: SEASON } });
      if (r.reopened > 0) toast.success(`Giornata ${md} riaperta (${r.reopened} tornei). Ora puoi ricalcolare.`);
      else toast.info(`Nessuna giornata ${md} chiusa da riaprire.`);
      setResult(null); setPreview(null);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const key = (fx) => `${fx.home_team}||${fx.away_team}`;

  const onSelect = async () => {
    const f = xlsxRef.current?.files?.[0];
    if (!f) return;
    setBusy(true); setResult(null); setPreview(null); setImported(false);
    try {
      const r = await apiUpload("/admin/voti/upload-xlsx", f, { dry_run: true, matchday_override: md });
      setFile(f);
      setVotiPreview({ matchday: r.matchday, players: r.players ?? r.rows?.length, rows: r.rows || [] });
      toast.success(`Anteprima voti: giornata ${r.matchday}`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); if (xlsxRef.current) xlsxRef.current.value = ""; }
  };

  const importVoti = async () => {
    if (!file) return;
    setBusy(true);
    try {
      await apiUpload("/admin/voti/upload-xlsx", file, { dry_run: false, replace: true, matchday_override: md });
      setImported(true);
      toast.success(`Voti giornata ${md} importati`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const calcPreview = async () => {
    setBusy(true); setResult(null);
    try {
      const body = { matchday: Number(md), season: SEASON };
      if (gamesFor) body.games = gamesFor;
      const r = await api("/admin/settle-matchday/preview", { method: "POST", body });
      setPreview(r);
      const e0 = {}; const p0 = {}; const s0 = {}; const pd0 = {};
      (r.fixtures?.list || []).forEach((fx) => {
        const k = key(fx);
        // Preserve scores already typed in this session (pre-fill on recalc).
        e0[k] = edits[k] ?? { home: fx.home_score ?? "", away: fx.away_score ?? "" };
        p0[k] = k in postponed ? postponed[k] : !!fx.postponed;
        s0[k] = k in suspended ? suspended[k] : !!fx.suspended;
        pd0[k] = k in pending ? pending[k] : !!fx.pending;
      });
      setEdits(e0); setPostponed(p0); setSuspended(s0); setPending(pd0);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const setScore = (k, side, val) => setEdits((p) => ({ ...p, [k]: { ...p[k], [side]: val } }));
  const togglePost = (k) => setPostponed((p) => {
    const nv = !p[k];
    if (nv) { setSuspended((s) => ({ ...s, [k]: false })); setPending((pd) => ({ ...pd, [k]: false })); }
    return { ...p, [k]: nv };
  });
  const toggleSus = (k) => setSuspended((s) => {
    const nv = !s[k];
    if (nv) { setPostponed((p) => ({ ...p, [k]: false })); setPending((pd) => ({ ...pd, [k]: false })); }
    return { ...s, [k]: nv };
  });
  const togglePending = (k) => setPending((pd) => {
    const nv = !pd[k];
    if (nv) { setPostponed((p) => ({ ...p, [k]: false })); setSuspended((s) => ({ ...s, [k]: false })); }
    return { ...pd, [k]: nv };
  });

  const publish = async () => {
    const list = preview?.fixtures?.list || [];
    const anyPending = list.some((fx) => !fx.excluded && pending[key(fx)]);
    const closes = !anyPending;
    const confirmMsg = anyPending
      ? `CALCOLO PARZIALE giornata ${md}: ci sono partite "in attesa". Aggiorno vite e classifiche dalle partite decise MA la giornata/torneo NON viene chiuso. Potrai ricalcolare quando inserisci i risultati mancanti. Procedo?`
      : mode === "sv_tk_bonus"
      ? `Chiudere la giornata ${md} e liquidare Survival + Ticket + Bonus (di entrambi)? Nessuna partita in attesa: verranno aggiornate vite, classifiche, premi Ticket e bonus e la giornata sarà CHIUSA.`
      : mode === "sv_bonus"
      ? `Chiudere la giornata ${md} e liquidare SOLO Survival e Bonus Big Match? Nessuna partita in attesa: la giornata sarà CHIUSA. (Il Ticket si calcola dalla sua stanza.)`
      : `Chiudere la giornata ${md} e liquidare Survival, Score, Fanta e Bonus? Il Ticket NON viene toccato (calcolo per-stanza).`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const postponed_matches = [];
      const suspended_matches = [];
      const pending_matches = [];
      const fixture_overrides = [];
      list.forEach((fx) => {
        const k = key(fx);
        if (fx.excluded) return; // eliminata dal calendario → non conteggiata
        if (pending[k]) {
          pending_matches.push({ home_team: fx.home_team, away_team: fx.away_team });
        } else if (postponed[k]) {
          postponed_matches.push({ home_team: fx.home_team, away_team: fx.away_team });
        } else if (suspended[k]) {
          const e = edits[k] || {};
          suspended_matches.push({
            home_team: fx.home_team, away_team: fx.away_team,
            home_score: Number(e.home || 0), away_score: Number(e.away || 0),
          });
        } else {
          const e = edits[k] || {};
          if (e.home !== "" && e.away !== "" && e.home != null && e.away != null) {
            fixture_overrides.push({ home_team: fx.home_team, away_team: fx.away_team, home_score: Number(e.home), away_score: Number(e.away) });
          } else {
            // Nessun risultato e non marcata → trattata come "in attesa".
            pending_matches.push({ home_team: fx.home_team, away_team: fx.away_team });
          }
        }
      });
      const body = { matchday: Number(md), season: SEASON, fixture_overrides, postponed_matches, suspended_matches, pending_matches };
      if (gamesFor) body.games = gamesFor;
      if (fsName.trim()) { body.first_scorer_player_name = fsName.trim(); body.first_scorer_team = fsTeam.trim() || null; }
      const r = await api("/admin/settle-matchday/commit", { method: "POST", body });
      setResult(r);
      toast.success(closes ? "Giornata chiusa e liquidata!" : "Calcolo parziale aggiornato (giornata ancora aperta)");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const fixtures = preview?.fixtures?.list || [];
  const anyPending = fixtures.some((fx) => !fx.excluded && pending[key(fx)]);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[#10B981]/40 bg-[#10B981]/5 p-4 space-y-3">
        <label className="text-xs text-[#94A3B8]">Giornata da calcolare</label>
        <input data-testid="cg-md" type="number" min="1" max="38" value={md} onChange={(e) => { setMdTouched(true); setMd(e.target.value); }} className="w-full bg-[#0F1216] border border-white/15 rounded-md px-3 py-2" />
        <label className="text-xs text-[#94A3B8]">Modalità di calcolo</label>
        <div className="grid gap-2">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input data-testid="cg-mode-full" type="radio" name="cg-mode" className="mt-0.5" checked={mode === "full"} onChange={() => changeMode("full")} />
            <span>Completa (con Excel voti) — <b>Survival + Score + Fanta + Bonus</b>. <span className="text-[#64748B]">Il Ticket si calcola dalla sua stanza.</span></span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input data-testid="cg-mode-sv" type="radio" name="cg-mode" className="mt-0.5" checked={mode === "sv_bonus"} onChange={() => changeMode("sv_bonus")} />
            <span>Manuale — solo <b className="text-[#EF4444]">Survival</b> + Bonus (senza Excel). <span className="text-[#64748B]">Il Ticket si calcola dalla sua stanza.</span></span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input data-testid="cg-mode-svtk" type="radio" name="cg-mode" className="mt-0.5" checked={mode === "sv_tk_bonus"} onChange={() => changeMode("sv_tk_bonus")} />
            <span>Manuale — <b className="text-[#EF4444]">Survival</b> + <b className="text-[#F59E0B]">Ticket</b> + Bonus di entrambi (senza Excel).</span>
          </label>
        </div>
        <div className="flex items-center justify-between gap-2 pt-1 border-t border-white/10 mt-1">
          <span className="text-xs text-[#64748B]">Hai chiuso la giornata per errore?</span>
          <button data-testid="cg-reopen" onClick={reopen} disabled={busy} className="text-xs font-bold text-[#F59E0B] border border-[#F59E0B]/40 rounded-md px-3 py-1.5 hover:bg-[#F59E0B]/10 disabled:opacity-50">Riapri giornata chiusa G{md}</button>
        </div>
      </div>

      {/* Step 1: Excel voti (non necessario in modalità manuale) */}
      {!manual && (
      <div className="rounded-xl border border-white/10 bg-[#181D22] p-4 space-y-3">
        <div className="font-bold">1. Carica Excel Voti</div>
        <input ref={xlsxRef} data-testid="cg-xlsx" type="file" accept=".xlsx" onChange={onSelect} className="hidden" />
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => xlsxRef.current?.click()} disabled={busy} className="border border-white/15 rounded-md px-4 py-2 text-sm hover:bg-white/5 disabled:opacity-50">Scegli file .xlsx</button>
          {votiPreview && !imported && <button data-testid="cg-import" onClick={importVoti} disabled={busy} className="bg-[#F59E0B] text-[#1A1000] font-bold text-sm rounded-md px-4 py-2 disabled:opacity-50">Importa voti G{md}</button>}
        </div>
        {votiPreview && <div className="text-sm text-[#94A3B8]">Rilevata giornata <b className="text-white">{votiPreview.matchday}</b> · {votiPreview.players} giocatori. {imported ? "✓ Importati" : "Premi «Importa voti» per salvare."}</div>}
      </div>
      )}

      {/* Step 2: preview + edit */}
      <div className="rounded-xl border border-white/10 bg-[#181D22] p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-bold">{manual ? "Inserisci i risultati" : "2. Anteprima risultati"}</div>
          <button data-testid="cg-calc" onClick={calcPreview} disabled={busy || (!imported && !manual)} className="border border-white/15 rounded-md px-4 py-2 text-sm disabled:opacity-50">Calcola anteprima</button>
        </div>
        {!imported && !manual && <p className="text-xs text-[#94A3B8]">Importa prima i voti.</p>}
        {manual && <p className="text-xs text-[#94A3B8]">Inserisci i risultati a mano: {mode === "sv_tk_bonus" ? "verranno liquidati Survival, il Ticket (tutte le stanze della giornata) e i Bonus." : "verranno liquidati solo Survival e il Bonus Big Match (il Ticket si calcola dalla sua stanza)."}</p>}
        {preview && (
          <>
            {(preview.warnings || []).length > 0 && (
              <div className="rounded-md bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-2 text-xs text-[#F59E0B] space-y-1">
                {preview.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
            <div className="space-y-2">
              {fixtures.map((fx) => {
                const k = key(fx);
                const isP = postponed[k];
                const isS = suspended[k];
                const isPend = pending[k];
                const isX = !!fx.excluded;
                const cls = isX ? "border-white/10 bg-[#0F1216] opacity-60" : isPend ? "border-[#3B82F6]/50 bg-[#3B82F6]/5" : isP ? "border-[#EF4444]/40 bg-[#EF4444]/5" : isS ? "border-[#F59E0B]/40 bg-[#F59E0B]/5" : "border-white/10 bg-[#0F1216]";
                return (
                  <div key={k} data-testid={`cg-fx-${k}`} className={`rounded-lg border p-2.5 ${cls}`}>
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="flex-1 min-w-[140px] truncate">{fx.home_team} <span className="text-[#94A3B8]">vs</span> {fx.away_team}
                        {isX && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-[#94A3B8] font-bold">ELIMINATA</span>}
                        {isPend && !isX && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#3B82F6]/20 text-[#93C5FD] font-bold">IN ATTESA</span>}
                      </span>
                      {isX ? (
                        <span className="text-xs text-[#94A3B8]">Non conteggiata (eliminata dal calendario)</span>
                      ) : (
                        <>
                          <input data-testid={`cg-h-${k}`} type="number" min="0" disabled={isP || isPend} value={edits[k]?.home ?? ""} onChange={(e) => setScore(k, "home", e.target.value)} className="w-12 bg-[#181D22] border border-white/15 rounded px-2 py-1 text-center disabled:opacity-40" />
                          <span className="text-[#94A3B8]">-</span>
                          <input data-testid={`cg-a-${k}`} type="number" min="0" disabled={isP || isPend} value={edits[k]?.away ?? ""} onChange={(e) => setScore(k, "away", e.target.value)} className="w-12 bg-[#181D22] border border-white/15 rounded px-2 py-1 text-center disabled:opacity-40" />
                          <label className="flex items-center gap-1 text-xs text-[#93C5FD] cursor-pointer ml-1">
                            <input data-testid={`cg-pend-${k}`} type="checkbox" checked={!!isPend} onChange={() => togglePending(k)} /> In attesa
                          </label>
                          <label className="flex items-center gap-1 text-xs text-[#94A3B8] cursor-pointer ml-1">
                            <input data-testid={`cg-post-${k}`} type="checkbox" checked={!!isP} onChange={() => togglePost(k)} /> Rinviata
                          </label>
                          <label className="flex items-center gap-1 text-xs text-[#F59E0B] cursor-pointer ml-1">
                            <input data-testid={`cg-sus-${k}`} type="checkbox" checked={!!isS} onChange={() => toggleSus(k)} /> Sospesa
                          </label>
                        </>
                      )}
                    </div>
                    {isPend && <div className="text-[11px] text-[#93C5FD] mt-1">In attesa: partita non ancora giocata. Calcolo PARZIALE — la giornata resta aperta (Ticket: quota 1.00).</div>}
                    {isS && <div className="text-[11px] text-[#F8C471] mt-1">Sospesa: inserisci il punteggio al momento dello stop. 1X2 nulli; Over/Gol/Multigol valutati secondo il betting.</div>}
                  </div>
                );
              })}
            </div>
            {preview.first_scorer_bonus_open && (
              <div className="rounded-lg border border-[#10B981]/30 bg-[#10B981]/5 p-3 space-y-2">
                <div className="text-sm font-bold text-[#10B981]">Bonus Primo Marcatore attivo</div>
                <div className="flex gap-2">
                  <input data-testid="cg-fs-name" value={fsName} onChange={(e) => setFsName(e.target.value)} placeholder="Nome primo marcatore" className="flex-1 bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-sm" />
                  <input data-testid="cg-fs-team" value={fsTeam} onChange={(e) => setFsTeam(e.target.value)} placeholder="Squadra (opz.)" className="w-32 bg-[#0F1216] border border-white/15 rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            )}
            {anyPending && (
              <div className="rounded-md bg-[#3B82F6]/10 border border-[#3B82F6]/30 p-2 text-xs text-[#93C5FD]">
                Ci sono partite <b>in attesa</b>: premendo il pulsante fai un <b>calcolo PARZIALE</b> — vite e classifiche si aggiornano ma la giornata/torneo <b>NON si chiude</b>. Rientra e premi <b>Ricalcola</b> quando inserisci i risultati mancanti. La giornata si chiude da sola quando non resta nessuna partita in attesa.
              </div>
            )}
            <button data-testid="cg-publish" onClick={publish} disabled={busy} className={`w-full font-bold rounded-md py-3 disabled:opacity-50 ${anyPending ? "bg-[#3B82F6] text-white" : "bg-[#EF4444] text-white"}`}>{anyPending ? `Ricalcola giornata G${md} (parziale, non chiude)` : `Chiudi e liquida G${md}`}</button>
          </>
        )}
      </div>

      {result && (() => {
        const svLogs = (result.log || []).filter((l) => l.game === "survival" && l.detail && typeof l.detail === "object");
        const anyPartial = svLogs.some((l) => l.detail.partial);
        return (
          <div className={`rounded-xl border p-4 space-y-2 ${anyPartial ? "border-[#3B82F6]/40 bg-[#3B82F6]/5" : "border-[#00D95F]/40 bg-[#00D95F]/5"}`}>
            <div className={`font-bold ${anyPartial ? "text-[#93C5FD]" : "text-[#00D95F]"}`}>{anyPartial ? "Calcolo parziale salvato — giornata ancora APERTA" : "Liquidazione completata — giornata CHIUSA"}</div>
            {anyPartial && <div className="text-xs text-[#93C5FD]">Vite e classifiche aggiornate dalle partite decise. Torna qui e premi Ricalcola quando inserisci i risultati mancanti.</div>}
            <pre className="text-xs bg-[#0F1216] rounded-md p-3 overflow-auto max-h-60 text-[#94A3B8]">{JSON.stringify(result.summary, null, 2)}</pre>
          </div>
        );
      })()}
    </div>
  );
}
